import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/ComboDisplay/OpponentSelector.css';
import { useChampions, Champion } from '../../hooks/useChampions';

interface StaticOpponentSelectorProps {
  championId: string;
  currentOpponent?: string;
  onOpponentChange?: (newOpponent: string) => void;
  championName?: string; // Main champion name for global updates
}

const StaticOpponentSelector: React.FC<StaticOpponentSelectorProps> = ({ 
  championId, 
  currentOpponent,
  onOpponentChange,
  championName
}) => {
  const { searchChampions } = useChampions();
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<Champion[]>([]);
  const [selectedChampion, setSelectedChampion] = useState<Champion | null>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update suggestions when search input changes
  useEffect(() => {
    if (searchInput.length > 0) {
      const results = searchChampions(searchInput);
      setSuggestions(results);
      
      // Auto-select first suggestion if available
      if (results.length > 0) {
        setSelectedChampion(results[0]);
      } else {
        setSelectedChampion(null);
      }
    } else {
      setSuggestions([]);
      setSelectedChampion(null);
    }
  }, [searchInput, searchChampions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setSearchInput(input);
    setIsDropdownVisible(true); // Show dropdown when typing
  };

  const handleInputFocus = () => {
    // Show dropdown when focusing if there are suggestions to show
    if (suggestions.length > 0) {
      setIsDropdownVisible(true);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && selectedChampion) {
      handleConfirmSelection();
    } else if (e.key === 'Escape') {
      // Close dropdown on Escape key
      setIsDropdownVisible(false);
      inputRef.current?.blur();
    }
  };

  const handleConfirmSelection = async () => {
    if (!selectedChampion || !championName) {
      console.error('No champion selected or champion name provided');
      return;
    }

    setLoading(true);

    try {
      console.log(`[StaticOpponentSelector] Setting opponent to: ${selectedChampion.name} for all rows in ${championName} grid`);
      
      // Send request to static API to update all opponents
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/grid/update-all-opponents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName: championName,
          newOpponentName: selectedChampion.id  // Send ID instead of display name
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log(`[StaticOpponentSelector] Successfully updated all opponents to ${selectedChampion.name}`);
        
        // Call the callback to notify parent component
        if (onOpponentChange) {
          onOpponentChange(selectedChampion.name);
        }

        // Clear the search after selection
        setSearchInput('');
        setSuggestions([]);
        setSelectedChampion(null);
        setIsDropdownVisible(false);
      } else {
        console.error(`[StaticOpponentSelector] Failed to update opponents:`, result.error);
      }

    } catch (error) {
      console.error(`[StaticOpponentSelector] Error updating opponents:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleChampionSelect = async (champion: Champion) => {
    setSelectedChampion(champion);
    setSearchInput(champion.name);
    setIsDropdownVisible(false);
    
    // Automatically confirm selection when clicking
    if (!championName) {
      console.error('No champion name provided for global update');
      return;
    }

    setLoading(true);

    try {
      console.log(`[StaticOpponentSelector] Setting opponent to: ${champion.name} for all rows in ${championName} grid`);
      
      // Send request to static API to update all opponents
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/grid/update-all-opponents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName: championName,
          newOpponentName: champion.id  // Send DDragon ID instead of display name
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log(`[StaticOpponentSelector] Successfully updated all opponents to ${champion.name}`);
        
        // Call the callback to notify parent component
        if (onOpponentChange) {
          onOpponentChange(champion.name);
        }

        // Clear after a short delay for visual feedback
        setTimeout(() => {
          setSearchInput('');
          setSelectedChampion(null);
        }, 100);
      } else {
        console.error(`[StaticOpponentSelector] Failed to update opponents:`, result.error);
      }

    } catch (error) {
      console.error(`[StaticOpponentSelector] Error updating opponents:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='opponent-selector-wrapper' ref={wrapperRef}>
      <div className='opponent-selector-bar'>
        <input
          ref={inputRef}
          type='text'
          placeholder="Search for Opponent Champion"
          value={searchInput}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className="opponent-selector-input"
          disabled={loading}
        />
        
        {suggestions.length > 0 && isDropdownVisible && (
          <ul className='opponent-suggestions-list'>
            {suggestions.map((champion) => (
              <li
                key={champion.id}
                className={`opponent-suggestion-item ${selectedChampion?.id === champion.id ? 'selected' : ''}`}
                onClick={() => handleChampionSelect(champion)}
              >
                <img src={champion.imageUrl} alt={champion.name} />
                <span>{champion.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {loading && (
        <div className="opponent-loading">
          Setting opponent...
        </div>
      )}
    </div>
  );
};

export default StaticOpponentSelector;
